import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { AccountingDepositDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-details-entity';
import { AccountingDepositEntity } from '../../../../../../api/types/accounting/accounting-deposit-entity';
import {
  AccountingDepositPaymentDetailsEntity,
  DepositPaymentCheckDetails,
} from '../../../../../../api/types/accounting/accounting-deposit-payment-details-entity';
import { AccountingDepositPaymentEntity } from '../../../../../../api/types/accounting/accounting-deposit-payment-entity';
import { DepositPaymentType } from '../../../../../../api/types/accounting/deposit-payment-type';
import { WarningDialog } from '../../../../../../component/ui/warning-dialog/warning-dialog';
import appApi from '../../../../../../api/app-api';
import { formatDate } from '../../../../../../utils/date-format';
import { downloadJournalEntryReportDatOperation } from '../../../../journal-entries/operations/download-journal-entry-report-dat-operation';
import { exportJournalEntryReportFlatDataPdfOperation } from '../../../../journal-entries/operations/export-journal-entry-report-flat-data-pdf-operation';
import { deleteDepositPaymentOperation } from '../operations/delete-deposit-payment-operation';
import { exportDepositReceiptsPdfOperation } from '../operations/export-deposit-receipts-pdf-operation';
import { exportDepositToExcelOperation } from '../operations/export-deposit-to-excel-operation';
import { getDepositDetailsOperation } from '../operations/get-deposit-details-operation';
import { lockAccountingDepositOperation } from '../operations/lock-accounting-deposit-operation';
import { EditDepositDateDialog } from './edit-deposit-date-dialog';

interface DepositDetailsDialogProps {
  visible: boolean;
  deposit: AccountingDepositDetailsEntity | null;
  onHide: () => void;
  // Fired when the deposit was mutated from inside the dialog (e.g. a payment
  // was deleted or the deposit date was edited). Lets the parent list refresh
  // its row so columns like "סכום כולל" reflect the change without a full
  // page refresh.
  onDepositChanged?: () => void;
}

const paymentTypeLabel = (type: DepositPaymentType): string => {
  switch (type) {
    case DepositPaymentType.CASH:
      return 'מזומן';
    case DepositPaymentType.CHECK:
      return 'המחאה';
    default:
      return type;
  }
};

const paymentTypeSeverity = (
  type: DepositPaymentType,
): 'success' | 'info' | 'warning' | 'danger' => {
  switch (type) {
    case DepositPaymentType.CASH:
      return 'success';
    case DepositPaymentType.CHECK:
      return 'warning';
    default:
      return 'info';
  }
};

const paymentSourceLabel = (
  payment: AccountingDepositPaymentEntity,
): string => {
  if (payment.userPaymentId !== null) return 'תשלום סטודנט';
  if (payment.userPaymentCheckDetailsId !== null) return 'המחאת סטודנט';
  if (payment.crmPaymentId !== null) return 'תשלום CRM';
  if (payment.crmCheckId !== null) return 'המחאה CRM';
  return 'חסר';
};

const paymentSourceIdLabel = (
  payment: AccountingDepositPaymentEntity,
): string | number => {
  if (payment.userPaymentId !== null) return payment.userPaymentId;
  if (payment.userPaymentCheckDetailsId !== null)
    return payment.userPaymentCheckDetailsId;
  if (payment.crmPaymentId !== null) return payment.crmPaymentId;
  if (payment.crmCheckId !== null) return payment.crmCheckId;
  return '—';
};

// Render a check-detail cell. Cash rows have `checkDetails: null` and any
// individual field can also be missing (e.g. `referenceId` is nullable on the
// entity), so we render a muted dash so columns line up cleanly across
// mixed cash/check tables.
const renderCheckDetail = (
  details: DepositPaymentCheckDetails | null,
  read: (d: DepositPaymentCheckDetails) => string | number | null | undefined,
): React.ReactNode => {
  if (!details) return <span style={{ color: '#9ca3af' }}>—</span>;
  const value = read(details);
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: '#9ca3af' }}>—</span>;
  }
  return value;
};

// Per-row monetary amount. The deposit-payment row entity exposes the amount
// in two type-specific fields (cash on the row itself, check on the nested
// check-details object), so we centralize the lookup here so the dialog and
// the Excel export stay in lockstep.
const rowAmountValue = (
  row: AccountingDepositPaymentDetailsEntity,
): number | null => {
  if (row.paymentType === DepositPaymentType.CASH) {
    return row.cashAmount;
  }
  if (row.paymentType === DepositPaymentType.CHECK) {
    return row.checkDetails?.amount ?? null;
  }
  return null;
};

// Matches the export operation — large enough to cover any realistic deposit.
const PAYMENT_TOTALS_FETCH_LIMIT = 100000;

const computeDepositPaymentTotals = (
  paymentRows: AccountingDepositPaymentDetailsEntity[],
): { cashTotalAmount: number; checkTotalAmount: number } => {
  let cashTotalAmount = 0;
  let checkTotalAmount = 0;
  for (const row of paymentRows) {
    const amount = rowAmountValue(row);
    if (amount === null) continue;
    if (row.paymentType === DepositPaymentType.CASH) {
      cashTotalAmount += amount;
    } else if (row.paymentType === DepositPaymentType.CHECK) {
      checkTotalAmount += amount;
    }
  }
  return { cashTotalAmount, checkTotalAmount };
};

const formatDepositAmount = (amount: number): string =>
  amount.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const DepositDetailsDialog: React.FC<DepositDetailsDialogProps> = ({
  visible,
  deposit,
  onHide,
  onDepositChanged,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadedDeposit, setLoadedDeposit] =
    useState<AccountingDepositEntity | null>(null);
  const [payments, setPayments] = useState<
    AccountingDepositPaymentDetailsEntity[]
  >([]);
  const [paymentsOffset, setPaymentsOffset] = useState<number>(0);
  const [paymentsLimit, setPaymentsLimit] = useState<number>(10);
  const [paymentsTotal, setPaymentsTotal] = useState<number>(0);
  const [cashTotalAmount, setCashTotalAmount] = useState<number | null>(null);
  const [checkTotalAmount, setCheckTotalAmount] = useState<number | null>(null);

  const [paymentToDelete, setPaymentToDelete] =
    useState<AccountingDepositPaymentDetailsEntity | null>(null);
  const [deletingPayment, setDeletingPayment] = useState<boolean>(false);

  const [exportingExcel, setExportingExcel] = useState<boolean>(false);
  const [exportingReceiptsPdf, setExportingReceiptsPdf] =
    useState<boolean>(false);
  const [exportingJournalPdf, setExportingJournalPdf] =
    useState<boolean>(false);
  const [downloadingJournalDat, setDownloadingJournalDat] =
    useState<boolean>(false);
  // Journal-entry report created when the deposit is locked. Null until lock.
  const [journalReportId, setJournalReportId] = useState<number | null>(null);

  // Multi-select for the receipts-PDF export. PrimeReact uses `dataKey` to
  // preserve selection across lazy pages, so this array can hold rows that
  // aren't currently visible. An empty array preserves the legacy "export
  // everything" behavior of the PDF button.
  const [selectedPayments, setSelectedPayments] = useState<
    AccountingDepositPaymentDetailsEntity[]
  >([]);

  const [showEditDateDialog, setShowEditDateDialog] = useState<boolean>(false);

  const [showLockDialog, setShowLockDialog] = useState<boolean>(false);
  const [lockingDeposit, setLockingDeposit] = useState<boolean>(false);

  // Tracks whether the deposit was mutated while the dialog was open, so we
  // only notify the parent (and trigger a list refresh) when something
  // actually changed.
  const depositMutatedRef = useRef<boolean>(false);

  const fetchPayments = (
    depositId: number,
    nextOffset: number,
    nextLimit: number,
  ) => {
    getDepositDetailsOperation(
      depositId,
      nextOffset,
      nextLimit,
      setLoadedDeposit,
      setPayments,
      setPaymentsTotal,
      setLoading,
    );
  };

  const fetchPaymentAmountTotals = async (depositId: number) => {
    try {
      const res = await appApi.getAccountingDepositById(
        depositId,
        0,
        PAYMENT_TOTALS_FETCH_LIMIT,
      );
      if (res.data.errCode) return;
      const allPayments = res.data.body?.payments?.data ?? [];
      const totals = computeDepositPaymentTotals(allPayments);
      setCashTotalAmount(totals.cashTotalAmount);
      setCheckTotalAmount(totals.checkTotalAmount);
      // Prefer a reportId from the full payment list so pagination cannot hide it.
      const reportIdFromPayments =
        allPayments.find((p) => p.reportId != null)?.reportId ?? null;
      setJournalReportId(
        typeof reportIdFromPayments === 'number' ? reportIdFromPayments : null,
      );
    } catch {
      // Header summary is supplementary; the paginated table remains the source
      // of truth for individual rows.
    }
  };

  useEffect(() => {
    if (!visible || !deposit) {
      setLoadedDeposit(null);
      setPayments([]);
      setPaymentsOffset(0);
      setPaymentsTotal(0);
      setCashTotalAmount(null);
      setCheckTotalAmount(null);
      setJournalReportId(null);
      setPaymentToDelete(null);
      setSelectedPayments([]);
      setShowEditDateDialog(false);
      setShowLockDialog(false);
      return;
    }
    setPaymentsOffset(0);
    setSelectedPayments([]);
    setShowEditDateDialog(false);
    setShowLockDialog(false);
    depositMutatedRef.current = false;
    fetchPayments(deposit.id, 0, paymentsLimit);
    fetchPaymentAmountTotals(deposit.id);
  }, [visible, deposit?.id]);

  const onPaymentsPageChange = (event: DataTablePageEvent) => {
    if (!deposit) return;
    const newOffset = event.first;
    const newLimit = event.rows;
    setPaymentsOffset(newOffset);
    setPaymentsLimit(newLimit);
    fetchPayments(deposit.id, newOffset, newLimit);
  };

  const onDeletePaymentClick = (
    payment: AccountingDepositPaymentDetailsEntity,
  ) => {
    setPaymentToDelete(payment);
  };

  const onConfirmDeletePayment = async () => {
    if (!deposit || !paymentToDelete) return;
    const deletedId = paymentToDelete.id;
    await deleteDepositPaymentOperation(
      deposit.id,
      deletedId,
      setDeletingPayment,
      async () => {
        depositMutatedRef.current = true;
        setPaymentToDelete(null);
        setSelectedPayments((prev) => prev.filter((p) => p.id !== deletedId));
        const isLastOnPage = payments.length === 1 && paymentsOffset > 0;
        const nextOffset = isLastOnPage
          ? Math.max(0, paymentsOffset - paymentsLimit)
          : paymentsOffset;
        if (isLastOnPage) setPaymentsOffset(nextOffset);
        fetchPayments(deposit.id, nextOffset, paymentsLimit);
        fetchPaymentAmountTotals(deposit.id);
      },
    );
  };

  const displayDeposit: AccountingDepositEntity | null =
    loadedDeposit ?? deposit ?? null;

  // Notify the parent list (so it can refresh the mutated row) before closing,
  // but only when the deposit was actually changed inside the dialog.
  const handleClose = () => {
    if (depositMutatedRef.current) {
      depositMutatedRef.current = false;
      onDepositChanged?.();
    }
    onHide();
  };

  const onConfirmLock = async () => {
    if (!displayDeposit) return;
    const depositId = displayDeposit.id;
    await lockAccountingDepositOperation(depositId, setLockingDeposit, () => {
      // Flag for the parent refresh, optimistically reflect the locked state
      // in the header, and re-fetch payments so any backend-side status
      // changes (checks → הופקד) are visible without reopening the dialog.
      depositMutatedRef.current = true;
      setShowLockDialog(false);
      setLoadedDeposit((prev) => (prev ? { ...prev, isLocked: true } : prev));
      fetchPayments(depositId, paymentsOffset, paymentsLimit);
      fetchPaymentAmountTotals(depositId);
    });
  };

  const handleExportExcel = async () => {
    if (!displayDeposit) return;
    await exportDepositToExcelOperation(
      displayDeposit.id,
      deposit,
      setExportingExcel,
    );
  };

  const handleExportReceiptsPdf = async () => {
    if (!displayDeposit) return;
    // Empty array → operation falls back to legacy "export everything" path.
    const selectedIds = selectedPayments.map((p) => p.id);
    await exportDepositReceiptsPdfOperation(
      displayDeposit.id,
      deposit,
      setExportingReceiptsPdf,
      selectedIds,
    );
  };

  // Fallback: if the full-list totals fetch hasn't set a reportId yet, use the
  // current page (locked deposits stamp the same reportId on every payment).
  const effectiveJournalReportId = useMemo(() => {
    if (journalReportId !== null) return journalReportId;
    const fromPage = payments.find((p) => p.reportId != null)?.reportId;
    return typeof fromPage === 'number' ? fromPage : null;
  }, [journalReportId, payments]);

  const journalExportDisabledReason =
    effectiveJournalReportId === null
      ? 'יש לנעול את ההפקדה לפני ייצוא פקודות היומן'
      : null;

  const anyJournalExportInFlight = exportingJournalPdf || downloadingJournalDat;

  const handleExportJournalPdf = () => {
    if (effectiveJournalReportId === null) return;
    exportJournalEntryReportFlatDataPdfOperation(
      effectiveJournalReportId,
      setExportingJournalPdf,
    );
  };

  const handleDownloadJournalDat = () => {
    if (effectiveJournalReportId === null) return;
    downloadJournalEntryReportDatOperation(
      effectiveJournalReportId,
      setDownloadingJournalDat,
    );
  };

  const styles = {
    body: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '16px',
      direction: 'rtl' as const,
    },
    headerCard: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '12px',
      padding: '12px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    headerItem: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2px',
    },
    headerLabel: {
      fontSize: '0.75rem',
      color: '#64748b',
    },
    headerValue: {
      fontSize: '0.95rem',
      color: '#0f172a',
      fontWeight: 600 as const,
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: 600 as const,
      color: '#1f2937',
      borderBottom: '1px solid #e5e7eb',
      paddingBottom: '4px',
    },
    notesBlock: {
      padding: '10px',
      background: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: '8px',
      color: '#92400e',
      whiteSpace: 'pre-wrap' as const,
    },
    spinnerWrap: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '24px',
    },
    countsRow: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap' as const,
      color: '#475569',
      fontSize: '0.9rem',
    },
    pill: {
      padding: '4px 10px',
      borderRadius: '999px',
      background: '#eef2ff',
      border: '1px solid #c7d2fe',
      color: '#3730a3',
      fontWeight: 600 as const,
    },
  };

  const dialogFooter = (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
      <Button
        icon="pi pi-file-pdf"
        tooltip={journalExportDisabledReason ?? 'ייצוא פקודות יומן לPDF'}
        tooltipOptions={{ position: 'top', showOnDisabled: true }}
        onClick={handleExportJournalPdf}
        loading={exportingJournalPdf}
        disabled={
          effectiveJournalReportId === null ||
          anyJournalExportInFlight ||
          loading
        }
        style={{
          backgroundColor: '#dc2626',
          borderColor: '#dc2626',
          height: '32px',
          width: '48px',
        }}
      />
      <Button
        icon="pi pi-download"
        tooltip={journalExportDisabledReason ?? 'הורדת קובץ לחשבשבת'}
        tooltipOptions={{ position: 'top', showOnDisabled: true }}
        onClick={handleDownloadJournalDat}
        loading={downloadingJournalDat}
        disabled={
          effectiveJournalReportId === null ||
          anyJournalExportInFlight ||
          loading
        }
        style={{
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          height: '32px',
          width: '48px',
        }}
      />
      <Button
        label="סגור"
        icon="pi pi-times"
        style={{ height: '32px' }}
        className="p-button-outlined p-button-secondary"
        onClick={handleClose}
      />
    </div>
  );

  return (
    <Dialog
      header={
        displayDeposit ? `פרטי הפקדה #${displayDeposit.id}` : 'פרטי הפקדה'
      }
      visible={visible}
      onHide={handleClose}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      // Widened from 780 → 1220 to accommodate the new check-detail columns
      // (סכום המחאה / בנק / סניף / חשבון / אסמכתא / תאריך פירעון / תאריך הפקדה).
      // `maxWidth: 95vw` keeps the dialog usable on smaller screens.
      style={{ width: '1320px', maxWidth: '95vw' }}
      footer={dialogFooter}
    >
      <div style={styles.body}>
        {!displayDeposit ? (
          <div style={styles.spinnerWrap}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        ) : (
          <>
            <div style={styles.headerCard}>
              <div style={styles.headerItem}>
                <span style={styles.headerLabel}>מספר הפקדה</span>
                <span style={styles.headerValue}>
                  {typeof displayDeposit.depositSerialNumber === 'number'
                    ? displayDeposit.depositSerialNumber
                    : 'חסר'}
                </span>
              </div>
              <div style={styles.headerItem}>
                <span
                  style={{
                    ...styles.headerLabel,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  תאריך הפקדה
                  <Button
                    icon="pi pi-pencil"
                    text
                    rounded
                    severity="secondary"
                    aria-label="עריכת תאריך הפקדה"
                    tooltip="עריכת תאריך הפקדה"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => setShowEditDateDialog(true)}
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      padding: 0,
                    }}
                  />
                </span>
                <span style={styles.headerValue}>
                  {displayDeposit.depositDate
                    ? formatDate(displayDeposit.depositDate as Date)
                    : 'חסר'}
                </span>
              </div>
              <div style={styles.headerItem}>
                <span style={styles.headerLabel}>סכום כולל</span>
                <span style={styles.headerValue}>
                  {typeof displayDeposit.totalAmount === 'number'
                    ? displayDeposit.totalAmount.toFixed(2)
                    : 'חסר'}{' '}
                  ש״ח
                </span>
              </div>
              <div style={styles.headerItem}>
                <span style={styles.headerLabel}>סטטוס</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Tag
                    value={displayDeposit.isLocked ? 'נעולה' : 'פתוחה'}
                    severity={displayDeposit.isLocked ? 'success' : 'warning'}
                  />
                  {!displayDeposit.isLocked && (
                    <Button
                      icon="pi pi-lock"
                      label="נעילת הפקדה"
                      severity="success"
                      outlined
                      tooltip="נעילת ההפקדה ויצירת פקודות יומן"
                      tooltipOptions={{ position: 'top' }}
                      onClick={() => setShowLockDialog(true)}
                      style={{ height: '1.9rem', padding: '0 10px' }}
                    />
                  )}
                </span>
              </div>
              <div style={styles.headerItem}>
                <span style={styles.headerLabel}>חשבון בנק</span>
                <span style={styles.headerValue}>
                  {deposit?.bankAccount?.bankName?.trim()
                    ? deposit.bankAccount.bankName
                    : 'חסר'}
                </span>
              </div>
              <div style={styles.headerItem}>
                <span style={styles.headerLabel}>נוצר על ידי</span>
                <span style={styles.headerValue}>
                  {displayDeposit.createdByName?.trim()
                    ? displayDeposit.createdByName
                    : 'חסר'}
                </span>
              </div>
              <div style={styles.headerItem}>
                <span style={styles.headerLabel}>תאריך יצירה</span>
                <span style={styles.headerValue}>
                  {displayDeposit.createdAt
                    ? formatDate(displayDeposit.createdAt as Date)
                    : 'חסר'}
                </span>
              </div>
              {deposit && (
                <div style={styles.headerItem}>
                  <span style={styles.headerLabel}>סיכום פריטים</span>
                  <div style={styles.countsRow}>
                    <span style={styles.pill}>
                      פריטים: {deposit.itemsCount}
                    </span>
                    <span style={styles.pill}>
                      מזומן: {deposit.cashCount}
                      {cashTotalAmount !== null &&
                        ` · ₪${formatDepositAmount(cashTotalAmount)}`}
                    </span>
                    <span style={styles.pill}>
                      המחאות: {deposit.checkCount}
                      {checkTotalAmount !== null &&
                        ` · ₪${formatDepositAmount(checkTotalAmount)}`}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {displayDeposit.notes?.trim() && (
              <div style={styles.notesBlock}>
                <div style={styles.headerLabel}>הערות</div>
                <div>{displayDeposit.notes}</div>
              </div>
            )}

            <div
              style={{
                ...styles.sectionTitle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <span>תשלומים בהפקדה ({paymentsTotal.toLocaleString()})</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  icon="pi pi-file-pdf"
                  tooltip={
                    selectedPayments.length > 0
                      ? `ייצוא ${selectedPayments.length} קבלות נבחרות לPDF`
                      : 'ייצוא קבלות לPDF'
                  }
                  tooltipOptions={{ position: 'top' }}
                  onClick={handleExportReceiptsPdf}
                  loading={exportingReceiptsPdf}
                  disabled={
                    !displayDeposit || exportingReceiptsPdf || exportingExcel
                  }
                  style={{
                    backgroundColor: '#dc2626',
                    borderColor: '#dc2626',
                    height: '32px',
                  }}
                />
                <Button
                  icon="pi pi-file-excel"
                  tooltip="ייצוא לאקסל"
                  tooltipOptions={{ position: 'top' }}
                  onClick={handleExportExcel}
                  loading={exportingExcel}
                  disabled={
                    !displayDeposit || exportingExcel || exportingReceiptsPdf
                  }
                  style={{
                    backgroundColor: '#10b981',
                    borderColor: '#10b981',
                    height: '32px',
                  }}
                />
              </div>
            </div>

            <DataTable
              value={payments}
              loading={loading}
              rowHover
              paginator
              lazy
              rows={paymentsLimit}
              first={paymentsOffset}
              totalRecords={paymentsTotal}
              onPage={onPaymentsPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              emptyMessage={
                <div style={{ textAlign: 'right' }}>
                  לא נמצאו תשלומים בהפקדה
                </div>
              }
              dataKey="id"
              selection={selectedPayments}
              onSelectionChange={(e) =>
                setSelectedPayments(
                  e.value as AccountingDepositPaymentDetailsEntity[],
                )
              }
              tableStyle={{ direction: 'rtl' }}
            >
              {/*              <Column
                field="id"
                header="מזהה"
                style={{ textAlign: 'right', width: '90px' }}
                headerStyle={{ textAlign: 'right' }}
              />*/}
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3rem' }}
                style={{ width: '3rem', textAlign: 'center' }}
              />
              <Column
                field="paymentType"
                header="סוג תשלום"
                style={{ textAlign: 'right', width: '120px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) => (
                  <Tag
                    value={paymentTypeLabel(row.paymentType)}
                    severity={paymentTypeSeverity(row.paymentType)}
                  />
                )}
              />
              <Column
                header="מקור"
                style={{ textAlign: 'right', width: '140px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  paymentSourceLabel(row)
                }
              />
              <Column
                header="שלוחות"
                style={{ textAlign: 'right', width: '180px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  row.branches && row.branches.length > 0
                    ? row.branches.map((b) => b.branchName).join(', ')
                    : '—'
                }
              />
              {/*              <Column
                header="מזהה מקור"
                style={{ textAlign: 'right', width: '120px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  paymentSourceIdLabel(row)
                }
              />*/}
              <Column
                header="סכום"
                style={{ textAlign: 'right', width: '100px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) => {
                  const amount = rowAmountValue(row);
                  return amount === null ? (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  ) : (
                    amount.toFixed(2)
                  );
                }}
              />
              <Column
                header="מספר קבלה"
                style={{ textAlign: 'right', width: '100px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) => {
                  return <label>{row.accountingDocument.docNumber}</label>;
                }}
              />
              {/*              <Column
                header="סכום המחאה"
                style={{ textAlign: 'right', width: '100px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  renderCheckDetail(row.checkDetails, (d) =>
                    d.amount.toFixed(2),
                  )
                }
              />*/}
              <Column
                header="בנק"
                style={{ textAlign: 'right', width: '80px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  renderCheckDetail(
                    row.checkDetails,
                    (d) => d.bankNumber?.trim() || null,
                  )
                }
              />
              <Column
                header="סניף"
                style={{ textAlign: 'right', width: '90px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  renderCheckDetail(
                    row.checkDetails,
                    (d) => d.branchNumber?.trim() || null,
                  )
                }
              />
              <Column
                header="חשבון"
                style={{ textAlign: 'right', width: '120px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  renderCheckDetail(
                    row.checkDetails,
                    (d) => d.accountNumber?.trim() || null,
                  )
                }
              />
              <Column
                header="אסמכתא"
                style={{ textAlign: 'right', width: '110px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  renderCheckDetail(
                    row.checkDetails,
                    (d) => d.referenceId?.trim() || null,
                  )
                }
              />
              <Column
                header="תאריך פירעון"
                style={{ textAlign: 'right', width: '120px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  renderCheckDetail(row.checkDetails, (d) =>
                    d.dueDate ? formatDate(d.dueDate as Date) : null,
                  )
                }
              />
              <Column
                header="תאריך הפקדה"
                style={{ textAlign: 'right', width: '120px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) => {
                  // For checks the row's "deposit date" is the check's clearing
                  // date (when the bank receives the funds). For cash there's
                  // no per-row date, so we surface the deposit-level date so
                  // accountants can scan a single column to see when each
                  // payment lands in the account.
                  if (row.paymentType === DepositPaymentType.CHECK) {
                    return renderCheckDetail(row.checkDetails, (d) =>
                      d.dueDate ? formatDate(d.dueDate as Date) : null,
                    );
                  }
                  return displayDeposit?.depositDate ? (
                    formatDate(displayDeposit.depositDate as Date)
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  );
                }}
              />
              <Column
                field="createdAt"
                header="תאריך יצירת הפקדה"
                style={{ textAlign: 'right', width: '140px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
                }
              />
              <Column
                header="קובץ"
                style={{ textAlign: 'right', width: '100px' }}
                headerStyle={{ textAlign: 'right' }}
                body={(row: AccountingDepositPaymentDetailsEntity) =>
                  row.accountingDocument?.fileUrl ? (
                    <a
                      href={row.accountingDocument.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2563eb',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <i className="pi pi-external-link" />
                      פתח
                    </a>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>חסר</span>
                  )
                }
              />
              <Column
                header=""
                style={{ textAlign: 'center', width: '70px' }}
                headerStyle={{ textAlign: 'center' }}
                body={(row: AccountingDepositPaymentDetailsEntity) => (
                  <Button
                    icon="pi pi-trash"
                    text
                    severity="danger"
                    tooltip="הסרת תשלום"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => onDeletePaymentClick(row)}
                  />
                )}
              />
            </DataTable>
          </>
        )}
      </div>

      <EditDepositDateDialog
        visible={showEditDateDialog}
        depositId={displayDeposit?.id ?? null}
        currentDate={(displayDeposit?.depositDate as Date | undefined) ?? null}
        onHide={() => setShowEditDateDialog(false)}
        onSuccess={(updated) => {
          // Sync the dialog's locally-cached deposit so the header reflects
          // the new date immediately, and flag the deposit as mutated so the
          // parent list refreshes its row when the dialog closes.
          depositMutatedRef.current = true;
          setLoadedDeposit((prev) =>
            prev ? { ...prev, depositDate: updated.depositDate } : updated,
          );
        }}
      />

      <WarningDialog
        header="הסרת תשלום מההפקדה"
        isVisible={paymentToDelete !== null}
        onHide={() => {
          if (!deletingPayment) setPaymentToDelete(null);
        }}
        addIcon
        content={
          paymentToDelete
            ? `האם אתה בטוח שברצונך להסיר את התשלום #${paymentToDelete.id} מההפקדה?`
            : 'האם אתה בטוח שברצונך להסיר את התשלום מההפקדה?'
        }
        dangerousContent={
          paymentToDelete
            ? `סוג תשלום: ${paymentTypeLabel(paymentToDelete.paymentType)} | מקור: ${paymentSourceLabel(paymentToDelete)}`
            : undefined
        }
        notInformation="לא ניתן לבטל פעולה זו. התשלום יחזור לרשימת הממתינים להפקדה."
        requiredTypeingToApproved="delete"
        confirmLabel="הסר תשלום"
        cancelLabel="ביטול"
        confirmLoading={deletingPayment}
        onConfirm={onConfirmDeletePayment}
      />

      <WarningDialog
        header="נעילת הפקדה"
        isVisible={showLockDialog}
        onHide={() => {
          if (!lockingDeposit) setShowLockDialog(false);
        }}
        addIcon
        content={
          displayDeposit
            ? `האם אתה בטוח שברצונך לנעול את הפקדה #${displayDeposit.id}?`
            : 'האם אתה בטוח שברצונך לנעול את ההפקדה?'
        }
        dangerousContent="פעולה זו אינה הפיכה."
        notInformation="עם אישור הפעולה המערכת תייצר באופן אוטומטי פקודות יומן שלא ניתן למחוק, ותעדכן את סטטוס כל ההמחאות בהפקדה זו ל'הופקד'."
        requiredTypeingToApproved="locked"
        confirmLabel="נעל הפקדה"
        cancelLabel="ביטול"
        confirmLoading={lockingDeposit}
        onConfirm={onConfirmLock}
      />
    </Dialog>
  );
};
